/**
 * ETA display with live countdown.
 *
 * Accepts ``etaSeconds`` (remaining seconds) and displays a
 * human-readable countdown that ticks down every second.
 */

import { useEffect, useState } from 'react';
import { Clock } from 'lucide-react';

interface Props {
  /** Estimated remaining seconds.  ``null`` when unavailable. */
  etaSeconds: number | null;
}

function formatDuration(totalSeconds: number): string {
  if (totalSeconds <= 0) return '< 1s';

  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = Math.floor(totalSeconds % 60);

  const parts: string[] = [];
  if (h > 0) parts.push(`${h}h`);
  if (m > 0) parts.push(`${m}m`);
  if (s > 0 || parts.length === 0) parts.push(`${s}s`);

  return parts.join(' ');
}

export function AnalysisETA({ etaSeconds }: Props) {
  const [remaining, setRemaining] = useState(etaSeconds ?? 0);

  // Reset when a new estimate arrives from the server.
  useEffect(() => {
    if (etaSeconds !== null && etaSeconds > 0) {
      setRemaining(etaSeconds);
    }
  }, [etaSeconds]);

  // Tick down every second.
  useEffect(() => {
    if (remaining <= 0) return;

    const timer = setInterval(() => {
      setRemaining((prev) => Math.max(0, prev - 1));
    }, 1_000);

    return () => clearInterval(timer);
  }, [remaining > 0]); // eslint-disable-line react-hooks/exhaustive-deps

  if (etaSeconds === null || etaSeconds <= 0) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-400 dark:text-gray-500">
        <Clock className="h-4 w-4" />
        <span>Estimating...</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
      <Clock className="h-4 w-4 text-blue-500" />
      <span>ETA: {formatDuration(remaining)}</span>
    </div>
  );
}
